<template>
  <div class="multisteps-form__panel border-radius-xl bg-white" data-animation="FadeIn">
    <h5 class="font-weight-bolder">{{ $t('mating.Male') }}</h5>
    <p class="mb-0 text-sm text-green" v-if="localFormData.maleLeased && selectedLeaseUBN">{{ $t('mating.Male_leased_from_UBN') }} {{ selectedLeaseUBN }}</p>
    <div class="multisteps-form__content">
      <div class="row mt-3">
        <div class="col-12">
          <div class="mb-4">
            <p class="fw-bold text-sm">{{ $t('mating.Selected_male') }} ({{ selectedMales.length }})</p>
            <div class="row">
              <div v-for="male in selectedMales" :key="male.id" class="col-md-6 mb-1">
                <div class="card border  text-sm border-success bg-light" @click="toggleSelect(male)">
                  {{ displayText(male) }}
                  <span 
                    class="text-danger position-absolute top-1 end-0"
                    style="cursor: pointer; font-weight: bold; font-size: 25px; margin-right: 10px;"
                    @click.stop="toggleSelect(male)"
                  >
                    ×
                  </span>
                </div>
              </div>
              <p class="text-danger text-sm" v-if="selectedMales.length === 0" style="margin-bottom: 2px !important;">
                {{ $t('common.No_male_selected') }}
              </p>
              <p class="text-danger text-sm" v-if="v$.selectedMaleId.$error">
                {{ v$.selectedMaleId.$errors[0].$message }}
              </p>
            </div>
          </div>
        </div>
        <div class="col-6">
          <!-- Search Input -->
          <div class="input-group input-group-static null">
            <label class="">{{ $t('mating.Search') }}</label>
            <input v-model="searchQuery" type="text" class="form-control form-control-default" :placeholder="$t('mating.Search_UBN_studbooknumber_animalnumber_name')"> 
          </div>
        </div>
        <div class="col-12 mt-3">
          <!-- Available Males -->
          <p class="fw-bold text-sm">{{ $t('mating.Available_males') }}</p>
          <div class="row">
            <div v-for="male in filteredMales" :key="male.id" class="col-md-6 mb-1">
              <div class="card border text-sm" :class="{ 'border-success bg-light': isSelected(male.id), 'cursor-pointer': true }" @click="toggleSelect(male)">
                {{ displayText(male) }}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="button-row d-flex mt-4">
        <button class="btn mb-0 bg-gradient-light btn-sm null null js-btn-prev" type="button" @click="validateAndPrevStep">{{ $t('common.Prev') }}</button>
        <button class="btn mb-0 btn-sm bg-gradient-success null null ms-auto js-btn-next" type="button" @click="validateAndNextStep">{{ $t('common.Next') }}</button>                
      </div>
    </div>
  </div>
</template>

<script>
  import { computed, reactive, ref, watch } from 'vue'
  import { helpers } from '@vuelidate/validators'
  import useVuelidate from '@vuelidate/core'
  import { useI18n } from 'vue-i18n'

  export default {
    name: "MatingMale",
    props: {
      males: Array,      
      formData: Object,
      prevStep: Function,
      nextStep: Function
    },
    setup(props, { emit }) {
      const { t } = useI18n()
      const localFormData = reactive(props.formData);

      const searchQuery = ref('')
      const selectedMaleId = ref(props.formData.maleId || '')

      const isSelected = (id) => selectedMaleId.value.includes(id)

      const toggleSelect = (male) => {
        const isAlreadySelected = selectedMaleId.value.includes(male.id)

        // If already selected, unselect it
        if (isAlreadySelected) {
          selectedMaleId.value = []
        } else {
          selectedMaleId.value = [male.id]
        }
      }

      const displayText = (f) =>        
        `${f.ubn} ${f.studbookNumber ? ` | ${f.studbookNumber}` : ''}${f.animalNumber ? ` | ${f.animalNumber}` : ''}${f.name ? ` (${f.name})` : ''}`

      const selectedMales = computed(() =>
        props.males.filter(f => selectedMaleId.value.includes(f.id))
      )

      const filteredMales = computed(() =>
        props.males.filter(f =>
          !selectedMaleId.value.includes(f.id) &&
          displayText(f).toLowerCase().includes(searchQuery.value.toLowerCase())
        )
      )

      // Vuelidate rules – require at least one selected female
      const rules = computed(() => ({
        selectedMaleId: {
          required: helpers.withMessage(t('common.Select_one_male'), value => value.length === 1)
        }
      }))
      
      const v$ = useVuelidate(rules, { selectedMaleId })

      const selectedLeaseUBN = computed(() => {
        return selectedMales.value.length > 0 ? selectedMales.value[0]?.ubn || '' : ''
      })

      const maleLeasedFromUBNId = computed(() => {
        return selectedMales.value.length > 0 ? selectedMales.value[0]?.ubnId || '' : ''
      })

      watch(localFormData, () => {
        emit('update:formData', localFormData)
      }, { deep: true })

      const validateAndPrevStep = async () => {

        if (selectedMales.value.length === 0) {
          props.prevStep();
          return;
        }

        v$.value.$touch()
        if (v$.value.$pending) {
          await v$.value.$validate()
        }
        if (!v$.value.$invalid) {
          localFormData.maleId = selectedMaleId.value
          localFormData.selectedLeaseUBN = selectedLeaseUBN.value
          localFormData.maleLeasedFromUBNId = maleLeasedFromUBNId.value
          props.prevStep()
        }
      }

      const validateAndNextStep = async () => {      
        v$.value.$touch()
        if (v$.value.$pending) {
          await v$.value.$validate()
        }
        if (!v$.value.$invalid) {
          localFormData.maleId = selectedMaleId.value
          localFormData.selectedLeaseUBN = selectedLeaseUBN.value
          localFormData.maleLeasedFromUBNId = maleLeasedFromUBNId.value
          props.nextStep()
        }
      }

      return {
        localFormData,
        searchQuery,
        selectedMaleId,
        selectedMales,
        filteredMales,
        isSelected,
        toggleSelect,
        displayText,
        v$,
        validateAndPrevStep,
        validateAndNextStep,
        selectedLeaseUBN
      }
    }    
};
</script>
<style scoped>
.card {
  transition: background-color 0.2s ease-in-out;
  cursor: pointer;
  position: relative;
  padding: 10px !important;
}
.cursor-pointer {
  cursor: pointer;
}
.bg-light {
  background-color: #f9f9f9 !important;
}
.border-success {
  border-color: #28a745 !important;
}
</style>